import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { formatMoment, toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Select, Spinner, TextInput,
} from '@shop/ui'

type Balance = {
  customerId: string
  code: string
  nameAr: string
  phone: string | null
  creditLimit: number | null
  balance: number
  lastPaymentAt: string | null
  lastChargeAt: string | null
  available: number | null
}

type StatementLine = {
  entryNo: string
  entryType: string
  occurredAt: string
  debit: number
  credit: number
  running: number
  note: string | null
}

type Statement = {
  customerId: string
  code: string
  nameAr: string
  phone: string | null
  creditLimit: number | null
  opening: number
  closing: number
  lines: StatementLine[]
}

const ENTRY_NAMES: Record<string, string> = {
  SALE: 'بيعٌ آجل',
  COLLECTION: 'سندُ قبض',
  RETURN: 'مرتجع',
  ADJUSTMENT: 'تسوية',
  REVERSAL: 'عكسُ سند',
}

const money = (value: number) => value.toFixed(3)

/**
 * الذممُ — <b>من عليه، وكم، ومنذ متى</b>.
 *
 * <p>
 * ⚠ <b>والرصيدُ يأتي من الخادم مجموعًا من الدفتر</b>، ولا تجمعه هذه الشاشة:
 * شاشتان تحسبان الرقمَ نفسَه تُعطيانه مرّتين بطريقتين، وصاحبُ المحلّ يقرأ
 * أحدَهما للزبون.
 * </p>
 */
export default function Receivables() {
  const { can } = useAuth()
  const [open, setOpen] = useState<string | null>(null)

  const balances = useQuery({
    queryKey: ['receivables'],
    queryFn: () => api<Balance[]>('/api/v1/receivables?onlyIndebted=true'),
  })

  const rows = balances.data ?? []
  const total = rows.reduce((sum, row) => sum + row.balance, 0)

  return (
    <div className="space-y-4">
      <Panel title="الذمم">
        {balances.isPending && <Spinner />}
        {balances.isError && <ErrorBanner error={balances.error} />}

        {balances.data && rows.length === 0 && <Empty>لا ذمّةَ على أحد.</Empty>}

        {rows.length > 0 && (
          <>
            <div className="mb-3 flex flex-wrap gap-4 text-sm">
              <span>
                المجموع:{' '}
                <b className="tabular" dir="ltr">{money(total)} JOD</b>
              </span>
              <span>عددُ المديونين: <b className="tabular">{rows.length}</b></span>
            </div>

            <ul className="space-y-2">
              {rows.map((row) => (
                <li key={row.customerId}>
                  <button
                    type="button"
                    onClick={() => setOpen(open === row.customerId ? null : row.customerId)}
                    className="block w-full rounded-md border border-line px-3 py-2 text-start hover:bg-surface"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm font-medium">{row.nameAr}</span>
                      <span className="tabular text-sm font-semibold" dir="ltr">
                        {money(row.balance)} JOD
                      </span>
                    </div>

                    <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                      {row.phone && <span dir="ltr">{row.phone}</span>}

                      {/*
                        ⚠ **والمتاحُ يُقال حين يوجد سقف** — «بلا سقف» حالةٌ
                          صريحةٌ لا فراغ.
                      */}
                      <span>
                        {row.creditLimit === null
                          ? 'بلا سقف'
                          : `المتاح: ${money(row.available ?? 0)}`}
                      </span>

                      {row.lastPaymentAt && <span>آخرُ دفعة: {formatMoment(row.lastPaymentAt)}</span>}
                    </div>
                  </button>

                  {open === row.customerId && (
                    <div className="mt-2 space-y-3">
                      {can(Perm.collectionCreate) && <CollectForm customer={row} />}
                      <StatementCard customerId={row.customerId} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>
    </div>
  )
}

/** سندُ قبض — <b>ويُطفئ من الذمّة في المعاملة نفسِها</b>. */
function CollectForm({ customer }: { customer: Balance }) {
  const queries = useQueryClient()
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState('CASH')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')

  const save = useMutation({
    mutationFn: () =>
      api('/api/v1/collections', {
        method: 'POST',
        body: {
          customerId: customer.customerId,
          amount: toNumber(amount) ?? 0,
          methodCode: method,
          referenceNo: reference.trim() === '' ? null : reference.trim(),
          note: note.trim() === '' ? null : note.trim(),
        },
      }),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ['receivables'] })
      await queries.invalidateQueries({ queryKey: ['statement'] })
      setAmount('')
      setReference('')
      setNote('')
    },
  })

  const value = toNumber(amount) ?? 0
  const ready = value > 0 && !save.isPending

  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="المبلغ" hint={`عليه ${money(customer.balance)}`}>
          <NumberInput value={amount} onChange={setAmount} placeholder={money(customer.balance)} />
        </Field>

        <Field label="الأسلوب">
          <Select value={method} onChange={setMethod}>
            <option value="CASH">نقدًا</option>
            <option value="CARD">بطاقة</option>
          </Select>
        </Field>

        {method === 'CARD' && (
          <Field label="رقمُ المرجع">
            <TextInput value={reference} onChange={setReference} dir="ltr" />
          </Field>
        )}
      </div>

      <div className="mt-3">
        <Field label="ملاحظة">
          <TextInput value={note} onChange={setNote} placeholder="دفعةٌ على الحساب" />
        </Field>
      </div>

      {/*
        ⚠ **والتحصيلُ نقدًا يحتاج ورديّةً مفتوحة** — يقولها الخادمُ، وتُقال هنا
          قبل الضغط: نقدٌ يدخل الدرجَ خارجَ ورديّةٍ لا يظهر في تقرير الإغلاق.
      */}
      {method === 'CASH' && (
        <div className="mt-2">
          <Banner tone="info">
            التحصيلُ نقدًا يدخل درجَ الورديّة المفتوحة — ويُطابَق عند إغلاقها.
          </Banner>
        </div>
      )}

      {save.isError && (
        <div className="mt-3">
          <ErrorBanner error={save.error} />
        </div>
      )}

      <div className="mt-3">
        <Button tone="primary" disabled={!ready} onClick={() => save.mutate()}>
          {save.isPending ? 'يُسجَّل…' : 'سجّل سندَ القبض'}
        </Button>
      </div>
    </div>
  )
}

/** كشفُ الحساب — <b>والرصيدُ الجاري محسوبٌ في الخادم</b>. */
function StatementCard({ customerId }: { customerId: string }) {
  const statement = useQuery({
    queryKey: ['statement', customerId],
    queryFn: () => api<Statement>(`/api/v1/receivables/${customerId}/statement`),
  })

  if (statement.isPending) {
    return <Spinner />
  }

  if (statement.isError) {
    return <ErrorBanner error={statement.error} />
  }

  const data = statement.data!

  return (
    <div className="rounded-md border border-line p-3">
      <h3 className="mb-2 text-sm font-medium">كشفُ الحساب</h3>

      {data.lines.length === 0 ? (
        <Empty>لا قيودَ بعد.</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-ink-soft">
              <tr>
                <th className="p-1 text-start">القيد</th>
                <th className="p-1 text-start">النوع</th>
                <th className="p-1 text-start">التاريخ</th>
                <th className="p-1 text-end">عليه</th>
                <th className="p-1 text-end">له</th>
                <th className="p-1 text-end">الرصيد</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((line) => (
                <tr key={line.entryNo} className="border-t border-line">
                  <td className="tabular p-1" dir="ltr">{line.entryNo}</td>
                  <td className="p-1">{ENTRY_NAMES[line.entryType] ?? line.entryType}</td>
                  <td className="p-1">{formatMoment(line.occurredAt)}</td>
                  <td className="tabular p-1 text-end" dir="ltr">
                    {line.debit > 0 ? money(line.debit) : '—'}
                  </td>
                  <td className="tabular p-1 text-end" dir="ltr">
                    {line.credit > 0 ? money(line.credit) : '—'}
                  </td>
                  <td className="tabular p-1 text-end font-medium" dir="ltr">
                    {money(line.running)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
